import type { QualityWeek } from '@lastrouter/contracts';
import { AlertTriangle } from '@lastrouter/ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatPercent } from '../lib/format.js';

/*
 * Taxa de resposta por semana, uma linha por assunto. Regras da skill de dataviz:
 * - até 3 séries, nas cores categóricas validadas (ordem fixa, cor segue o assunto, não a posição);
 * - linha de 2 px, pontos de 8 px com anel da cor da superfície, grade em linha fina e recessiva;
 * - a semana que pede revisão leva um anel extra e, no tooltip e na tabela, ícone e texto (nunca só cor);
 * - legenda sempre presente com 2 ou mais séries; a visão em tabela fica ao lado (o aqua passa 3:1 só no escuro).
 */

export const MAX_SERIES = 3;
const SERIES_VARS = ['--series-1', '--series-2', '--series-3'] as const;

interface ChartColors {
  series: string[];
  grid: string;
  axis: string;
  text: string;
  surface: string;
  danger: string;
}

function readColors(): ChartColors {
  const css = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  return {
    series: SERIES_VARS.map((n, i) => v(n, ['#2a78d6', '#eb6834', '#1baf7a'][i]!)),
    grid: v('--chart-grid', '#e1e0d9'),
    axis: v('--chart-axis', '#c3c2b7'),
    text: v('--text-secondary', '#52514e'),
    surface: v('--surface', '#ffffff'),
    danger: v('--danger', '#b42318'),
  };
}

/** Cores do tema atual; troca quando o tema muda (classe no <html> ou sistema). */
function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(readColors);
  useEffect(() => {
    const update = () => setColors(readColors());
    window.addEventListener('lastrouter:tema', update);
    const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
    mq?.addEventListener('change', update);
    return () => {
      window.removeEventListener('lastrouter:tema', update);
      mq?.removeEventListener('change', update);
    };
  }, []);
  return colors;
}

export interface Series {
  id: string;
  label: string;
}

type Row = { week: string } & Record<string, number | string | boolean | null>;

export function toRows(weeks: QualityWeek[], series: Series[]): Row[] {
  const byWeek = new Map<string, Row>();
  for (const w of weeks) {
    const key = w.subject_id ?? '';
    if (!series.some((s) => s.id === key)) continue;
    const row = byWeek.get(w.week) ?? { week: w.week };
    row[key] = w.answer_rate;
    row[`${key}__review`] = w.needs_review;
    row[`${key}__n`] = w.questions;
    byWeek.set(w.week, row);
  }
  return [...byWeek.values()].sort((a, b) => a.week.localeCompare(b.week));
}

interface TooltipProps {
  active?: boolean;
  payload?: readonly { payload?: unknown }[];
  label?: string | number;
  series: Series[];
  colors: ChartColors;
}

function ChartTooltip({ active, payload, label, series, colors }: TooltipProps) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as Row;
  return (
    <div className="min-w-48 rounded-[var(--lr-radius-md)] border border-border bg-surface p-3 text-[length:var(--lr-text-sm)] text-fg shadow-lg">
      <p className="mb-2 font-semibold">{label}</p>
      <ul className="flex flex-col gap-1">
        {series.map((s, i) => {
          const v = row[s.id];
          if (typeof v !== 'number') return null;
          return (
            <li key={s.id} className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-0.5 w-3 rounded-full" style={{ background: colors.series[i] }} />
              <span className="flex-1">{s.label}</span>
              <span className="tabular font-semibold">{formatPercent(v)}</span>
              {row[`${s.id}__review`] ? (
                <span className="flex items-center gap-1 text-warning">
                  <AlertTriangle className="size-3.5" aria-hidden="true" />
                  {t('quality.review')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function AnswerRateChart({ weeks, series }: { weeks: QualityWeek[]; series: Series[] }) {
  const { t } = useTranslation();
  const colors = useChartColors();
  const shown = series.slice(0, MAX_SERIES);
  const rows = useMemo(() => toRows(weeks, shown), [weeks, shown]);
  const anyReview = rows.some((r) => shown.some((s) => r[`${s.id}__review`]));

  return (
    <figure className="flex flex-col gap-3">
      <div className="h-72 w-full" role="img" aria-label={`${t('quality.chartTitle')}: ${shown.map((s) => s.label).join(', ')}`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 12, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid vertical={false} stroke={colors.grid} strokeWidth={1} />
            <XAxis dataKey="week" tickFormatter={(w: string) => w.slice(5)} stroke={colors.axis} tick={{ fill: colors.text, fontSize: 12 }} tickLine={false} />
            <YAxis
              domain={[0, 1]}
              ticks={[0, 0.25, 0.5, 0.75, 1]}
              tickFormatter={(v: number) => formatPercent(v, 0)}
              stroke={colors.axis}
              tick={{ fill: colors.text, fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            <Tooltip content={(p) => <ChartTooltip active={p.active} payload={p.payload as TooltipProps['payload']} label={p.label as string | number | undefined} series={shown} colors={colors} />} cursor={{ stroke: colors.axis, strokeWidth: 1 }} />
            {shown.map((s, i) => (
              <Line
                key={s.id}
                dataKey={s.id}
                name={s.label}
                type="linear"
                stroke={colors.series[i]}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                isAnimationActive={false}
                connectNulls
                activeDot={{ r: 6, stroke: colors.surface, strokeWidth: 2, fill: colors.series[i] }}
                dot={(props: { cx?: number; cy?: number; index?: number; payload?: Row }) => {
                  const { cx, cy, index, payload } = props;
                  if (cx === undefined || cy === undefined || typeof payload?.[s.id] !== 'number') return <g key={`${s.id}-${index}`} />;
                  const review = Boolean(payload[`${s.id}__review`]);
                  return (
                    <g key={`${s.id}-${index}`}>
                      {review ? <circle cx={cx} cy={cy} r={8} fill="none" stroke={colors.danger} strokeWidth={2} /> : null}
                      <circle cx={cx} cy={cy} r={4} fill={colors.series[i]} stroke={colors.surface} strokeWidth={2} />
                    </g>
                  );
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <figcaption>
        <ul className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[length:var(--lr-text-sm)] text-fg-secondary">
          {shown.map((s, i) => (
            <li key={s.id} className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-0.5 w-4 rounded-full" style={{ background: colors.series[i] }} />
              <span className="inline-block size-2 -ml-3 rounded-full" aria-hidden="true" style={{ background: colors.series[i] }} />
              {s.label}
            </li>
          ))}
          {anyReview ? (
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block size-3 rounded-full border-2" style={{ borderColor: colors.danger }} />
              <AlertTriangle className="size-3.5 text-warning" aria-hidden="true" />
              {t('quality.review')}
            </li>
          ) : null}
        </ul>
      </figcaption>
    </figure>
  );
}
